import React from 'react';
import { useNavigate } from 'react-router-dom';
import './TermsPage.css';

function TermsPage() {
  const navigate = useNavigate();

  return (
    <div className="terms-container">
      <div className="terms-content">
        <button className="terms-back-btn" onClick={() => navigate(-1)}>
          ← Back
        </button>
        
        <h1>Terms and Conditions</h1>
        <p className="terms-effective-date"><strong>Effective Date:</strong> January 30, 2026 | <strong>Version:</strong> 1.0</p>

        <section>
          <h2>1. Introduction</h2>
          <p>These Terms and Conditions ("Terms") govern your access to and use of the 3AM AI service ("Service"), operated by 3AM AI ("we," "us," or "our"). By accessing or using the Service, you agree to be bound by these Terms.</p>
        </section>

        <section>
          <h2>2. Service Description</h2>
          <p>3AM AI is a conversational AI application designed to help users reflect on their thoughts and experiences through guided dialogue. The Service uses artificial intelligence to engage in conversations with users.</p>
        </section>

        <section>
          <h2>3. Critical Disclaimers</h2>
          
          <h3>3.1 Not a Medical or Therapeutic Service</h3>
          <p><strong>IMPORTANT:</strong> 3AM AI is NOT a medical service, mental health service, therapy, counseling, or healthcare service of any kind. The Service:</p>
          <ul>
            <li>Does NOT provide medical advice, diagnosis, or treatment</li>
            <li>Does NOT replace professional mental health care</li>
            <li>Is NOT a substitute for therapy, counseling, or psychiatric care</li>
            <li>Does NOT establish a doctor-patient, therapist-client, or any professional healthcare relationship</li>
            <li>Is NOT staffed by licensed healthcare professionals</li>
            <li>Should NOT be used for medical or mental health emergencies</li>
          </ul>

          <h3>3.2 Emergency Situations</h3>
          <p><strong>IF YOU ARE EXPERIENCING A MEDICAL OR MENTAL HEALTH EMERGENCY:</strong></p>
          <ul>
            <li><strong>Call 000</strong> (Emergency Services - Australia)</li>
            <li><strong>Call Lifeline: 13 11 14</strong> (24/7 Crisis Support)</li>
            <li><strong>Call Beyond Blue: 1300 22 4636</strong> (Mental Health Support)</li>
            <li><strong>Go to your nearest hospital emergency department</strong></li>
          </ul>
          <p>The Service is not designed for, and must not be used in, crisis or emergency situations.</p>

          <h3>3.3 AI Limitations</h3>
          <p>The Service uses artificial intelligence which can:</p>
          <ul>
            <li>Make mistakes or provide inaccurate information</li>
            <li>Misunderstand context or intent</li>
            <li>Generate inappropriate or incorrect responses</li>
            <li>Not detect serious mental health conditions or crises</li>
          </ul>
          <p>You acknowledge that AI-generated responses should not be relied upon for important decisions.</p>
        </section>

        <section>
          <h2>4. Eligibility</h2>
          <p>You must:</p>
          <ul>
            <li>Be at least 18 years of age</li>
            <li>Have the legal capacity to enter into binding contracts</li>
            <li>Reside in a jurisdiction where use of the Service is lawful</li>
            <li>Not be prohibited from accessing the Service under Australian law</li>
          </ul>
        </section>

        <section>
          <h2>5. Account Terms</h2>
          
          <h3>5.1 Registration</h3>
          <p>You must provide accurate, current, and complete information during registration and keep your account information updated.</p>

          <h3>5.2 Account Security</h3>
          <p>You are responsible for:</p>
          <ul>
            <li>Maintaining the confidentiality of your account credentials</li>
            <li>All activities that occur under your account</li>
            <li>Notifying us immediately of any unauthorized access</li>
          </ul>

          <h3>5.3 Account Termination</h3>
          <p>We reserve the right to suspend or terminate your account at any time for:</p>
          <ul>
            <li>Violation of these Terms</li>
            <li>Fraudulent or illegal activity</li>
            <li>Harm to other users or the Service</li>
            <li>Any reason at our sole discretion</li>
          </ul>
        </section>

        <section>
          <h2>6. User Responsibilities</h2>
          <p>You agree NOT to:</p>
          <ul>
            <li>Use the Service for medical, mental health, or healthcare purposes</li>
            <li>Rely on the Service in emergency or crisis situations</li>
            <li>Share personal information of others without consent</li>
            <li>Use the Service to harm, harass, or threaten others</li>
            <li>Attempt to reverse engineer, hack, or compromise the Service</li>
            <li>Use the Service for illegal purposes</li>
            <li>Violate any applicable laws or regulations</li>
            <li>Impersonate others or misrepresent your identity</li>
          </ul>
        </section>

        <section>
          <h2>7. Privacy and Data Collection</h2>
          <p>We collect, use, and store your personal information as described in our Privacy Policy. By using the Service, you consent to such collection and use. Your data includes:</p>
          <ul>
            <li>Account information (email, profile details)</li>
            <li>Conversation history</li>
            <li>Usage patterns and metrics</li>
          </ul>
          <p>Your conversations are stored and may be used to improve the Service.</p>
        </section>

        <section>
          <h2>8. Intellectual Property</h2>
          
          <h3>8.1 Our Rights</h3>
          <p>All content, features, and functionality of the Service, including but not limited to text, graphics, logos, software, and AI models, are owned by us or our licensors and are protected by intellectual property laws.</p>

          <h3>8.2 Your Content</h3>
          <p>You retain ownership of content you submit to the Service. By using the Service, you grant us a worldwide, royalty-free license to use, store, and process your content to provide and improve the Service.</p>
        </section>

        <section>
          <h2>9. Limitation of Liability</h2>
          
          <h3>9.1 Australian Consumer Law</h3>
          <p>Nothing in these Terms excludes, restricts, or modifies any guarantee, warranty, term, condition, right, or remedy implied or imposed by the Competition and Consumer Act 2010 (Cth) or any other applicable Australian consumer protection legislation that cannot lawfully be excluded, restricted, or modified ("Non-Excludable Rights").</p>

          <h3>9.2 Service Provided "As Is"</h3>
          <p>Subject to section 9.1, the Service is provided "as is" and "as available" without warranties of any kind, whether express or implied, including but not limited to:</p>
          <ul>
            <li>Warranties of merchantability or fitness for a particular purpose</li>
            <li>Accuracy, reliability, or completeness of content</li>
            <li>Uninterrupted or error-free operation</li>
            <li>Security or freedom from viruses or harmful components</li>
          </ul>

          <h3>9.3 Liability Cap</h3>
          <p>Subject to section 9.1, to the maximum extent permitted by law, our total liability to you for any claims arising from or related to the Service shall not exceed the amount you paid us in the 12 months prior to the claim, or AUD $100, whichever is greater.</p>

          <h3>9.4 Exclusion of Consequential Damages</h3>
          <p>Subject to section 9.1, we shall not be liable for:</p>
          <ul>
            <li>Indirect, incidental, special, consequential, or punitive damages</li>
            <li>Loss of profits, revenue, data, or use</li>
            <li>Personal injury or property damage</li>
            <li>Any harm resulting from reliance on AI-generated content</li>
            <li>Any harm resulting from use of the Service in lieu of professional care</li>
          </ul>
        </section>

        <section>
          <h2>10. Indemnification</h2>
          <p>You agree to indemnify, defend, and hold harmless 3AM AI, its officers, directors, employees, and agents from any claims, damages, losses, liabilities, and expenses (including legal fees) arising from:</p>
          <ul>
            <li>Your use or misuse of the Service</li>
            <li>Your violation of these Terms</li>
            <li>Your violation of any rights of another party</li>
            <li>Your reliance on AI-generated content for medical or health decisions</li>
          </ul>
        </section>

        <section>
          <h2>11. Termination</h2>
          
          <h3>11.1 Your Rights</h3>
          <p>You may terminate your account at any time by contacting us or ceasing to use the Service.</p>

          <h3>11.2 Our Rights</h3>
          <p>We may terminate or suspend your access immediately, without prior notice, for:</p>
          <ul>
            <li>Breach of these Terms</li>
            <li>Illegal activity</li>
            <li>Suspected fraud or security risks</li>
            <li>Any reason at our sole discretion</li>
          </ul>

          <h3>11.3 Effect of Termination</h3>
          <p>Upon termination:</p>
          <ul>
            <li>Your right to access the Service ceases immediately</li>
            <li>We may delete your account and data</li>
            <li>Provisions that should survive termination will remain in effect</li>
          </ul>
        </section>

        <section>
          <h2>12. Changes to Terms</h2>
          <p>We may modify these Terms at any time. Changes will be effective:</p>
          <ul>
            <li>Immediately upon posting for new users</li>
            <li>30 days after notification for existing users</li>
          </ul>
          <p>Continued use after changes constitutes acceptance. If you disagree with changes, you must stop using the Service.</p>
        </section>

        <section>
          <h2>13. Governing Law and Jurisdiction</h2>
          <p>These Terms are governed by the laws of New South Wales, Australia. Any disputes shall be subject to the exclusive jurisdiction of the courts of New South Wales, Australia.</p>
        </section>

        <section>
          <h2>14. Severability</h2>
          <p>If any provision of these Terms is found invalid or unenforceable, that provision shall be limited or eliminated to the minimum extent necessary, and the remaining provisions shall remain in full force and effect.</p>
        </section>

        <section>
          <h2>15. Entire Agreement</h2>
          <p>These Terms constitute the entire agreement between you and 3AM AI regarding the Service and supersede all prior agreements and understandings.</p>
        </section>

        <section>
          <h2>16. No Waiver</h2>
          <p>Our failure to enforce any right or provision of these Terms shall not constitute a waiver of such right or provision.</p>
        </section>

        <section>
          <h2>17. Contact Information</h2>
          <p>For questions about these Terms, contact us at:</p>
          <p><strong>Email:</strong> support@3amai.com.au<br />
          <strong>Address:</strong> 39 Devlin Street, Ryde NSW 2112, Australia</p>
        </section>

        <section>
          <h2>18. Acknowledgment</h2>
          <p>By accepting these Terms, you acknowledge that:</p>
          <ul>
            <li>You have read and understood these Terms</li>
            <li>3AM AI is not a medical or therapeutic service</li>
            <li>You will not use the Service for emergencies or healthcare purposes</li>
            <li>AI can make mistakes and should not be relied upon for important decisions</li>
            <li>You have been advised to seek professional help for medical or mental health concerns</li>
            <li>You use the Service at your own risk</li>
          </ul>
        </section>

        <p className="terms-last-updated"><strong>Last Updated:</strong> January 30, 2026</p>
      </div>
    </div>
  );
}

export default TermsPage;
